/**
 * LanguageContext - V17.16.47 Bilingual Support
 * V17.16.73: Added backend sync for notification localization
 *
 * Provides shared language state across the Transportation workspace.
 * Default language is Lao ('lo'), with English ('en') as toggle option.
 * Language preference is synced with backend for notification localization.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../services/api';

export type Language = 'lo' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  isLoading: boolean;
}

// Exported for provider-optional consumers (e.g. utils/ratingLabels
// useRatingLanguage), which fall back to the 'lo' default when rendered
// outside the provider (standalone component tests).
export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  // V17.16.47: Lao is the default language
  // V17.28.FIX: Force 'lo' default and load from localStorage first
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first, default to 'lo' (Lao)
    const saved = localStorage.getItem('vtw_language');
    return (saved === 'en' || saved === 'lo') ? saved : 'lo';
  });
  const [isLoading, setIsLoading] = useState(false);

  // V17.520 (Phase 0c, brief 2026-10-26-001, F24): <html lang> follows the app language, so
  // screen readers and the browser's font/hyphenation pick Lao or English correctly.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // V17.16.73: Load user's language preference from backend on mount
  // V17.28.FIX: Only load from backend if localStorage doesn't have preference
  useEffect(() => {
    const loadLanguagePreference = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return; // Not logged in

        // Skip backend load if localStorage already has preference
        const savedLang = localStorage.getItem('vtw_language');
        if (savedLang) return;

        const response = await apiClient.get('/users/me/language');
        if (response.data?.language_preference) {
          const lang = response.data.language_preference as Language;
          setLanguageState(lang);
          localStorage.setItem('vtw_language', lang);
        }
      } catch {
        // Silently fail - use default language
        console.debug('Could not load language preference, using default');
      }
    };

    loadLanguagePreference();
  }, []);

  // V17.16.73: Sync language preference to backend
  // V17.28.FIX: Also save to localStorage for persistence
  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('vtw_language', lang); // V17.28.FIX: Persist to localStorage
    setIsLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await apiClient.put(`/users/me/language?language=${lang}`);
      }
    } catch {
      // Silently fail - local state is still updated
      console.debug('Could not sync language preference to backend');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'lo' ? 'en' : 'lo';
    setLanguage(newLang);
  }, [language, setLanguage]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
